/**
 * The editor's identity row: name, country (fixed), color swatches, scope.
 * Private scope is visible but disabled until Drop Generator containers
 * exist — a private group must name its owner.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Shield, Users } from 'lucide-react';

import { Input, Tooltip } from '@/components/ui';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';
import { cn } from '@/lib/utils';

import { LIST_GROUP_COLOR_PRESETS } from '../../list-groups.types';

interface Props {
  name: string;
  color: string;
  countryName: string;
  onName: (name: string) => void;
  onColor: (color: string) => void;
}

const FIELD_LABEL =
  'text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground';

export const EditorFields: FC<Props> = ({
  name,
  color,
  countryName,
  onName,
  onColor,
}) => (
  <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
    <label className="flex w-56 flex-col gap-1.5">
      <span className={FIELD_LABEL}>Name</span>
      <Input value={name} onChange={(e) => onName(e.target.value)} placeholder="e.g. REG chasers" />
    </label>
    <div className="flex flex-col gap-1.5">
      <span className={FIELD_LABEL}>Country</span>
      <span className="flex h-10 items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground">
        {countryToFlagEmoji(countryName)} {countryName}
      </span>
    </div>
    <div className="flex flex-col gap-1.5">
      <span className={FIELD_LABEL}>Color</span>
      <div className="flex h-10 items-center gap-2">
        {LIST_GROUP_COLOR_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            aria-label={`Color ${preset}`}
            onClick={() => onColor(preset)}
            className={cn(
              'h-5 w-5 rounded-full transition-transform hover:scale-110',
              color === preset && 'ring-2 ring-primary ring-offset-2 ring-offset-card',
            )}
            style={{ backgroundColor: preset }}
          />
        ))}
        <label
          aria-label="Custom color"
          title="Pick any color"
          className={cn(
            'relative h-5 w-5 cursor-pointer rounded-full transition-transform hover:scale-110',
            !(LIST_GROUP_COLOR_PRESETS as readonly string[]).includes(color) &&
              'ring-2 ring-primary ring-offset-2 ring-offset-card',
          )}
          style={
            (LIST_GROUP_COLOR_PRESETS as readonly string[]).includes(color)
              ? { background: 'conic-gradient(#ef4444,#f59e0b,#22c55e,#06b6d4,#6366f1,#d946ef,#ef4444)' }
              : { backgroundColor: color }
          }
        >
          <input
            type="color"
            value={color}
            onChange={(e) => onColor(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </label>
      </div>
    </div>
    <div className="flex flex-col gap-1.5">
      <span className={FIELD_LABEL}>Scope</span>
      <div className="inline-flex h-10 items-center rounded-md border border-border/60 bg-muted/30 p-0.5">
        <span className="inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-sm">
          <Users className="h-3.5 w-3.5" /> Shared
        </span>
        <Tooltip content="Private groups arrive with Automation containers">
          <span className="inline-flex cursor-not-allowed items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground/50">
            <Shield className="h-3.5 w-3.5" /> Private
          </span>
        </Tooltip>
      </div>
    </div>
  </div>
);
